 /// <reference path="../../typings/browser.d.ts" />

 namespace Application.Services {
    'use strict';

    import TsProbe = Models.TsProbe;
    import ITsProbe = Models.ITsProbe;
    import ITsProbeGui = Models.ITsProbeGui;


    export type ProbeCallback = (findByITResult: Models.IFindByTsResults) => void;
    export class TsProbeService {
        public static $name: string = "tsProbeService";
        public static $inject: string[] = [
            "backendService",
            "$q",
            "$interval",
            "flexCollectorService",
            "networkNodeService",
            "aclService",
        ];

        private backendBaseUrl: string = window.backendUrl;
        private tsProbeUrl: string = this.backendBaseUrl + 'tsprobe';
        private probedBlocks: {[key: number]: boolean} = {};
        private allProbedBlocks: Models.IAllProbedBlocks = undefined;
        private unProbed: Models.IFindByTsResults = {
            tsProbesFound: [],
            defaultTsProbe: undefined,
            selectedStream: undefined,
        };
        private subscriptions: {[blockId: number]: ProbeCallback[]} = {};

        private tsProbeGuiMap: ng.IPromise<ITsProbeGui>[] = [];
        private probes: Models.TsProbe[];
        private loadingProbes: boolean = false;
        private waitingForLoadingProbes: ng.IDeferred<TsProbe[]>[] = [];
        private intervalPromise: ng.IPromise<void>;
        private blocksNeedProbeCall: {[key: number]: boolean} = {};

        constructor(private backendService: BackendService,
                    private $q: ng.IQService,
                    private $interval: ng.IIntervalService,
                    private flexCollectorService: Services.FlexCollectorService,
                    private networkNodeService: Services.NetworkNodeService,
                    private aclService: Services.AclService) {
        }

        public getProbesCached(): ng.IPromise<TsProbe[]> {
            const deferred: ng.IDeferred<TsProbe[]> = this.$q.defer();
            if (!this.probes) {
                if (!this.loadingProbes) {
                    //This will be in charge of loading a notifying the rest of the requesters
                    this.makeTsProbesBackendRequest(deferred);
                } else {
                    //Adding my promise to the queue to be resolved by the first one that came
                    this.waitingForLoadingProbes.push(deferred);
                }
            } else {
                //Probes already loaded, resolving right away
                deferred.resolve(this.probes);
            }
            return deferred.promise;
        }

        private makeTsProbesBackendRequest(deferred: ng.IDeferred<TsProbe[]>): void {
            this.loadingProbes = true;
            this.getAll().then((probes: TsProbe[]) => {
                this.$q.all(probes.map((probe: TsProbe) => {
                    return this.aclService.getPermissions('networkNodes', probe.networkNodeId)
                        .then((evaluatedAcls: Models.IEvaluatedAcl) => {
                        if (evaluatedAcls.read && evaluatedAcls.write) {
                            probe.permissions = evaluatedAcls;
                        }
                    });
                })).then(() => {
                    this.probes = probes;
                    this.loadingProbes = false;
                    deferred.resolve(this.probes);
                    this.waitingForLoadingProbes.forEach((otherDeferred: ng.IDeferred<TsProbe[]>) => {
                        otherDeferred.resolve(this.probes);
                    });
                });
            });
        }

        public getAll(): ng.IPromise<TsProbe[]> {
            return this.backendService.get(this.tsProbeUrl + "/")
                .then((response: ng.IHttpPromiseCallbackArg<ITsProbe[]>) => {
                    return response.data.map((entity: ITsProbe) => {
                        return TsProbe.Copy(entity);
                    });
                }).catch(()=>{
                    return [];
                });
        }

        public getAllWithNodesAndType(type: Models.NetworkNodeType): ng.IPromise<Models.ISelectableProbe[]> {
            return this.backendService.get(this.tsProbeUrl + "/withNodes")
                .then((response: ng.IHttpPromiseCallbackArg<Models.ISelectableProbe[]>) => {
                    return response.data.map((entity: Models.ISelectableProbe) => {
                        let node: Models.NetworkNode = Models.NetworkNode.Copy(entity.node);
                        node.type = type;
                        node = this.networkNodeService.burnishNode(node);
                        return {
                            probe: Models.TsProbe.Copy(entity.probe),
                            node: node,
                        }
                    });
                });
        }


        public getAllProbedBlocks(): ng.IPromise<Models.IAllProbedBlocks> {
            return this.backendService.get(this.tsProbeUrl + '/allProbedBlocks')
                .then((findResult: ng.IHttpPromiseCallbackArg<Models.IAllProbedBlocks>) => {
                    const allProbedBlocks = findResult.data;
                    const visitedBlocks: {[key: number]: boolean} = {};
                    Object.keys(allProbedBlocks.allProbedBlocks).forEach((blockId: string) => {
                        visitedBlocks[blockId] = true;
                        const findByTsResults: Models.IFindByTsResults = allProbedBlocks.allProbedBlocks[blockId];
                        findByTsResults.tsProbesFound.forEach((probeFound: Models.ITsProbeFound) => {
                            delete probeFound.tsProbe.tsInputs;
                        });
                        this.probedBlocks[blockId] = true;
                        if (this.subscriptions[blockId]) {
                            this.subscriptions[blockId].forEach((callback: ProbeCallback) => {
                                callback(findByTsResults);
                            });
                        }
                    });
                    //Removing blockIds not visited
                    Object.keys(this.probedBlocks).forEach((blockId: string) => {
                        if (visitedBlocks[blockId] === undefined) {
                            visitedBlocks[blockId] = true;
                            delete this.probedBlocks[blockId];
                            if (this.subscriptions[blockId]) {
                                this.subscriptions[blockId].forEach((callback: ProbeCallback) => {
                                    callback(undefined);
                                });
                            }
                        }
                    });
                    // Signal the rest of the subscriptions not in the probed blocks
                    Object.keys(this.subscriptions).forEach((blockId: string) => {
                        if (visitedBlocks[blockId] === undefined) {
                            this.subscriptions[blockId].forEach((callback: ProbeCallback) => {
                                callback(undefined);
                            });
                        }
                    });
                    this.allProbedBlocks = allProbedBlocks;
                    return allProbedBlocks;
                });
        }

        public getConnectedProbeByBlockId(blockId: number, probedBlocks?: Models.IAllProbedBlocks): ng.IPromise<Models.IFindByTsResults> {
            if (probedBlocks !== undefined) {
                this.allProbedBlocks = probedBlocks;
            }
            if (this.allProbedBlocks) {
                return this.$q.resolve(this.allProbedBlocks.allProbedBlocks[blockId]);
            } else {
                return this.$q.resolve(this.unProbed);
            }
        }

        public subscribeConnectedProbeByBlockId(blockId: number, callback: (findByTsResults: Models.IFindByTsResults) => void): () => void {
            if (!this.subscriptions[blockId])
                this.subscriptions[blockId] = [];
            this.subscriptions[blockId].push(callback);
            return () => {
                const index = this.subscriptions[blockId].indexOf(callback);
                this.subscriptions[blockId].splice(index, 1);
            }
        }

        public getConnectedProbeByBlockIdSingle(blockId: number): ng.IPromise<Models.IFindByTsResults> {
            return this.backendService.get(this.tsProbeUrl + '/byBlock/' + blockId, false)
                .then((findResult: ng.IHttpPromiseCallbackArg<Models.IFindByTsResults>) => {
                    return findResult.data;
                });
        }

        public connectProbeByBlockId(blockId: number, probeId: number): ng.IPromise<Models.ProbeOperationResult> {
            return this.backendService.post(this.tsProbeUrl + '/' + probeId + '/probe/' + blockId, {}, false, undefined, false)
                .then((probe: ng.IHttpPromiseCallbackArg<Models.ProbeOperationResult>) => {
                    this.getAllProbedBlocks();
                    return probe.data;
                });
        }

        public switchProbesByBlockId(blockId: number, changes: Models.ITsChange[]): ng.IPromise<Models.ProbeOperationResult> {
            return this.backendService.put(this.tsProbeUrl + '/probe/' + blockId, changes, false, undefined, false)
                .then((probe: ng.IHttpPromiseCallbackArg<Models.ProbeOperationResult>) => {
                    this.getAllProbedBlocks();
                    return probe.data;
                });
        }

        public switchDefaultProbe(blockId: number, networkNodeId: number): ng.IPromise<boolean> {
            return this.backendService.put(`${this.tsProbeUrl}/probe/${blockId}/${networkNodeId}`, {}, false, undefined, false)
                .then((probe: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return probe.data;
                });
        }

        public disconnectProbeByBlockId(blockId: number): ng.IPromise<Models.ProbeOperationResult> {
            return this.backendService.delete(this.tsProbeUrl + '/byBlock/' + blockId, false)
                .then((probe: ng.IHttpPromiseCallbackArg<Models.ProbeOperationResult>) => {
                    this.getAllProbedBlocks();
                    return probe.data;
                });
        }

        public activateAudioMonByBlockId(blockId: number): ng.IPromise<boolean> {
            return this.backendService.post(this.tsProbeUrl + '/audioMon/' + blockId, false)
                .then((probe: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return probe.data;
                }).catch((reason: any) => {
                    console.log(reason);
                    return false;
                });
        }

        public disableAudioMonByBlockId(blockId: number): ng.IPromise<boolean> {
            return this.backendService.delete(this.tsProbeUrl + '/audioMon/' + blockId, false)
                .then((probe: ng.IHttpPromiseCallbackArg<boolean>) => {
                    return probe.data;
                });
        }

        public getAudioLevelsFromTNS(probeNodeId: number): ng.IPromise<Models.DeviceParam[]> {
            return this.flexCollectorService.getDeviceParamsBySimplifiedHref(`networkNodes/${probeNodeId}`)
                .then(((result: Models.DeviceParams) => {
                    return result.params.filter((param: Models.DeviceParam) => param.name.indexOf("audio") > -1);
                })).catch((reason: any) => {
                    console.log(reason);
                    return [];
                });
        }

        public getProbeById(probeId: number): ng.IPromise<TsProbe> {
            return this.backendService.get(this.tsProbeUrl + '/' + probeId)
                .then((probeResponse: ng.IHttpPromiseCallbackArg<ITsProbe>) => {
                    return TsProbe.Copy(probeResponse.data);
                });
        }

        public getProbeGuiByProbeId(probeId: number): ng.IPromise<ITsProbeGui> {
            if (this.tsProbeGuiMap[ probeId ] !== undefined ) {
                return this.tsProbeGuiMap[probeId];
            }
            let promise: ng.IPromise<ITsProbeGui> =
                this.backendService.get(this.tsProbeUrl + '/' + probeId + '/gui')
                    .then((response: ng.IHttpPromiseCallbackArg<ITsProbeGui>) => {
                        return response.data;
                    });
            this.tsProbeGuiMap[probeId] = promise;
            return promise;
        }

        public getTSProbeGuiSectionUrl( probeId: number, section: string, slot?: number ): ng.IPromise<String> {
            // Calculating on the frontend to avoid requests
            // let url: string = this.tsProbeUrl + '/' + probeId + '/gui/' + section;
            // url += slot ? '?slot=' + slot : '';
            // return this.backendService.get( url )
            //     .then((response: ng.IHttpPromiseCallbackArg<any>) => { // tslint:disable-line
            //         return response.data.url;
            //     });
            return this.$q.resolve("#mt=3&sin=30000" + slot + "&tab=1&tbbi=2");
        }

        public stopTsProbeInterval(blockId: number): void {
            if (blockId !== undefined && this.blocksNeedProbeCall[blockId]) {
                this.blocksNeedProbeCall[blockId] = false;
                if (!this.isAnyBlockUsingProbesCall()) {
                    if (this.intervalPromise !== undefined
                        && this.intervalPromise['$$state']
                        && this.intervalPromise['$$state'].status !== Application.Common.PROMISE_REJECTED) {
                        this.$interval.cancel(this.intervalPromise);
                    }
                }
            }
        }

        public initTsProbeInterval(blockId: number): void {
            if (blockId !== undefined && !this.blocksNeedProbeCall[blockId]) {
                this.blocksNeedProbeCall[blockId] = true;
                if (this.intervalPromise === undefined
                    || (this.intervalPromise['$$state'] && this.intervalPromise['$$state'].status !== Application.Common.PROMISE_PENDING)) {
                        this.intervalPromise = this.$interval(() => {
                            this.getAllProbedBlocks();
                        }, 3000);
                }
            }

        }

        private isAnyBlockUsingProbesCall(): boolean {
            if (this.blocksNeedProbeCall !== undefined) {
                let result: string[] = Object.keys(this.blocksNeedProbeCall).filter( (blockId: string) => {
                    return this.blocksNeedProbeCall[blockId] === true;
                });
                return result.length > 0;
            }
            return false;
        }

    }


}

